import type { OutDesignAST, OutDesignNode } from "./types"
import type { PandocJson, Block, Inline, Attr, AttrList } from "../pandoc"
import { containsBlocks } from "../rules/utils"
import { Caption, ListAttributes } from "../pandoc/types"

export function emptyAttr(): Attr {
  return ["", [], []]
}

export function hasAttr(attr?: Attr): boolean {
  if (!attr) return false
  const [id, classes, kv] = attr
  return !!id || classes.length > 0 || kv.length > 0
}

function isWrapper(elt: Block | Inline): boolean {
  if(elt.t == "Div" || elt.t == "Span") {
    const attrList: AttrList = elt.c[0][2]
    for(const entry of attrList) {
      if(entry[0] === "wrapper" && entry[1] === "1") return true;
    }
  }
  return false
}

function removeAttr(attr: Attr, name: string): Attr {
  const attrList: AttrList = attr[2]
  let newAttrList: AttrList = []
  for(const entry of attrList) {
    if(entry[0] !== name) newAttrList.push(entry)
  }
  return [attr[0], attr[1], newAttrList];
}

function addAttr(attr: Attr, name: string, value: string): Attr {
  return [attr[0], attr[1], [...attr[2], [name, value]]]
}

/* ------------------------------------------------ */
/*         Pandoc → OutDesign conversion            */
/* ------------------------------------------------ */

export async function pandocToOutDesign(doc: PandocJson): Promise<OutDesignAST> {
  const ast: OutDesignAST = {
    meta: doc.meta,
    "pandoc-api-version": doc["pandoc-api-version"],
    blocks: doc.blocks.map(blockToOutDesignNode)
  }
  return ast;
}

function persistOriginalAttr(node: OutDesignNode): OutDesignNode {
  if(node.data) {
    node.data.originalAttr = structuredClone(node.attr)
  } else {
    node.data = {
      originalAttr: structuredClone(node.attr)
    }
  }
  return node
}

function blockToOutDesignNode(block: Block): OutDesignNode {
  return persistOriginalAttr(parseBlock(block))
}

function parseBlock(block: Block): OutDesignNode {

  switch (block.t) {
    
    case "Para":
    return {
      type: "Para",
      attr: emptyAttr(),
      children: block.c.map(inlineToOutDesignNode)
    }

    case "Plain":
    return {
      type: "Plain",
      attr: emptyAttr(),
      children: block.c.map(inlineToOutDesignNode)
    }
    
    case "Header": {
      const [level, attr, inlines] = block.c
      
      return {
        type: "Header",
        attr,
        data: { level: level },
        children: inlines.map(inlineToOutDesignNode)
      }
    }
    
    case "Div": {
      const [attr, blocks] = block.c
      if(isWrapper(block) && blocks.length > 0) {
        if (containsBlocks(blocks[0].t)) {
          return {
            type: blocks[0].t,
            attr: removeAttr(attr, "wrapper"),
            children: (blocks[0].c as Block[]).map(blockToOutDesignNode)
          }
        } else {
          // In that case, Blocks are actually inlines!
          const single_child: Inline[] = blocks[0].c as Inline[]
          return {
            type: blocks[0].t,
            attr: removeAttr(attr, "wrapper"),
            children: single_child.map(inlineToOutDesignNode)
          }
        }
      } else {
        return {
          type: "Div",
          attr,
          children: blocks.map(blockToOutDesignNode)
        }
      }
    }
    
    case "BlockQuote":
    return {
      type: "BlockQuote",
      attr: emptyAttr(),
      children: block.c.map(blockToOutDesignNode)
    }

    case "CodeBlock":
    const [attr, text] = block.c
    return {
      type: "CodeBlock",
      attr: attr,
      data: { text: text }
    }

    case "OrderedList": {
    const [attrs, items] = block.c

    return {
        type: "OrderedList",
        attr: emptyAttr(),
        data: {
          start: attrs[0],
          style: attrs[1].t,
          delim: attrs[2].t
        },
        children: items.map(item =>
          item.map(blockToOutDesignNode)
        )
      }
    }

    case "BulletList":
    return {
      type: "BulletList",
      attr: emptyAttr(),
      children: block.c.map(item =>
        item.map(blockToOutDesignNode)
      )
    }

    case "LineBlock":
    return {
      type: "LineBlock",
      attr: emptyAttr(),
      children: block.c.map(line =>
        line.map(inlineToOutDesignNode)
      ) 
    }

    case "Figure":
      const [figattr, caption, blocks] = block.c
    return {
      type: "Figure",
      attr: figattr,
      data: { caption: caption },
      children: blocks.map(blockToOutDesignNode)
    }

    case "DefinitionList":
    return {
      type: "DefinitionList",
      children: block.c as unknown
      // we do nothing, it is just so that the tests pass!
    }

    case "RawBlock":
      const [format, rawtext] = block.c
      return {
        type: "RawBlock",
        attr: emptyAttr(),
        data: {
          format: format,
          text: rawtext
        }
      }

    default:
    return {
      type: block.t,
      attr: emptyAttr()
    }
  }
}

function inlineToOutDesignNode(inline: Inline): OutDesignNode {
  return persistOriginalAttr(parseInline(inline))
}

function parseInline(inline: Inline): OutDesignNode {
  
  switch (inline.t) {
    
    case "Str":
    return {
      type: "Str",
      data: { text: inline.c }
    }
    
    case "Space":
    case "SoftBreak":
    case "LineBreak":
    return {
      type: inline.t
    }
    
    case "Emph":
    case "Strong":
    case "Strikeout":
    case "Underline":
    case "Superscript":
    case "Subscript":
    case "SmallCaps":
    return {
      type: inline.t,
      attr: emptyAttr(),
      children: inline.c.map(inlineToOutDesignNode)
    }
    
    case "Code": {
      return {
        type: "Code",
        attr: inline.c[0],
        data: { text: inline.c[1] }
      }
    }

    case "Quoted": {
      const [qt, inlines] = inline.c
      
      return {
        type: "Quoted",
        attr: emptyAttr(),
        data: { quoteType: qt.t },
        children: inlines.map(inlineToOutDesignNode)
      }
    }
    
    case "Link": {
      const [attr, inlines, target] = inline.c
      
      return {
        type: "Link",
        attr,
        data: {
          url: target[0],
          title: target[1]
        },
        children: inlines.map(inlineToOutDesignNode)
      }
    }
    
    case "Image": {
      const [attr, alt, target] = inline.c
      
      return {
        type: "Image",
        attr,
        data: {
          src: target[0],
          title: target[1],
          alt: alt.map(inlineToOutDesignNode)
        }
      }
    }

    case "Span": {
      const [attr, inlines] = inline.c
      if(isWrapper(inline) && inlines.length > 0) {
        const single_child: Inline[] = inlines[0].c as Inline[]
        return {
          type: inlines[0].t,
          attr: removeAttr(attr, "wrapper"),
          children: single_child.map(inlineToOutDesignNode)
        }
      } else {
        return {
          type: "Span",
          attr,
          children: inlines.map(inlineToOutDesignNode)
        }
      }
    }
    
    case "Note":
    return {
      type: "Note",
      attr: emptyAttr(),
      children: inline.c.map(blockToOutDesignNode)
    }
    
    default:
    return {
      type: inline.t,
      attr: emptyAttr(),
      data: { text: "Hello" }
    }
  }
}

/* ------------------------------------------------ */
/*        OutDesign → Pandoc conversion             */
/* ------------------------------------------------ */

export function outDesignToPandoc(ast: OutDesignAST): PandocJson {
  return {
    meta: ast.meta,
    "pandoc-api-version": ast["pandoc-api-version"],
    blocks: ast.blocks.map(outDesignBlockToPandoc)
  }
}

function wrapBlockIfNeeded(node: OutDesignNode, block: Block): Block {
  if (!hasAttr(node.attr)) return block

  return {
    t: "Div",
    c: [
      addAttr(node.attr as Attr, "wrapper", "1"),
      [block]
    ]
  }
}

function wrapInlineIfNeeded(node: OutDesignNode, inline: Inline): Inline {
  if (!node.attr || !hasAttr(node.attr)) return inline

  return {
    t: "Span",
    c: [
      addAttr(node.attr as Attr, "wrapper", "1"),
      [inline]
    ]
  }
}

function outDesignBlockToPandoc(node: OutDesignNode): Block {
  switch (node.type) {

    case "Para": {
      const para: Block = {
        t: "Para",
        c: (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
      }
      return wrapBlockIfNeeded(node, para)
    }

    case "Plain": {
      const para: Block = {
        t: "Plain",
        c: (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
      }
      return wrapBlockIfNeeded(node, para)
    }

    case "BlockQuote": {
      const bq: Block = {
        t: "BlockQuote",
        c: (node.children as OutDesignNode[]).map(outDesignBlockToPandoc) ?? []
      }
      return wrapBlockIfNeeded(node, bq)
    }

    case "Header":
      return {
        t: "Header",
        c: [
          node.data?.level as number ?? 1,
          node.attr ?? emptyAttr(),
          (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
        ]
      }

    case "Div":
    // Does not take into account when there is a "wrapper=1" attr
    return {
      t: "Div",
      c: [
        node.attr ?? emptyAttr(),
        (node.children as OutDesignNode[]).map(outDesignBlockToPandoc) ?? []
      ]
    }

    case "CodeBlock":
    return {
      t: "CodeBlock",
      c: [
        node.attr ?? emptyAttr(),
        node.data?.text as string ?? ""
      ]
    }

    case "OrderedList":
      return {
        t: "OrderedList",
        c: [
          [
            node.data?.start ?? 1,
            { t: node.data?.style ?? "DefaultStyle" },
            { t: node.data?.delim ?? "DefaultDelim" }
          ] as ListAttributes,
          (node.children as any[]).map(item =>
            (Array.isArray(item) ? item : [item]).map(outDesignBlockToPandoc)
          )
        ]
      }

    case "BulletList":
      return {
        t: "BulletList",
        c: (node.children as any[]).map(item =>
          (Array.isArray(item) ? item : [item]).map(outDesignBlockToPandoc)
        )
      }

    case "LineBlock":
      return {
        t: "LineBlock",
        c: (node.children as OutDesignNode[][]).map(item =>
          item.map(outDesignInlineToPandoc)
        )
      }

    case "Figure":
      return {
        t: "Figure",
        c: [
          node.attr ?? emptyAttr(),
          node.data?.caption as Caption,
          (node.children as OutDesignNode[]).map(outDesignBlockToPandoc) ?? []
        ]
      }

    case "DefinitionList":
      return {
        t: "DefinitionList",
        c: node.children as unknown as Array<[Array<Inline>, Array<Array<Block>>]>
      }

    case "RawBlock":
      return {
        t: "RawBlock",
        c: [ node.data?.format as string, node.data?.text as string]
      }

    default:
      // throw new Error(`Unsupported block type: ${node.type}`)
    return {
      t: "Para",
      c: [{ t: "Str", c: "[NOT SUPPORTED]" }] as Inline[]
    }
  }
}

function outDesignInlineToPandoc(node: OutDesignNode): Inline {
  
  switch (node.type) {
    
    case "Str":
    return { t: "Str", c: node.data?.text as string ?? "" }
    
    case "Space":
    case "SoftBreak":
    case "LineBreak":
    return { t: node.type } as Inline
    
    // currently, it just drops the attributes on element
    // when converted back to the Pandoc AST
    case "Emph":
    case "Strong":
    case "Strikeout":
    case "Underline":
    case "Superscript":
    case "Subscript":
    case "SmallCaps":
      const emph: Inline = {
        t: node.type,
        c: (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
      }

      return wrapInlineIfNeeded(node, emph)
    
    case "Code":
      return {
        t: "Code",
        c: [
          node.attr ?? emptyAttr(),
          node.data?.text as string
        ]
      }

    case "Quoted":
      return {
        t: "Quoted", // 
        c: [
          { t: (node.data?.quoteType) },
          (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
        ]
      }

    case "Link":
    return {
      t: "Link",
      c: [
        node.attr ?? emptyAttr(),
        (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? [],
        [node.data?.url as string ?? "", node.data?.title as string ?? ""]
      ]
    }
    
    case "Image":
    return {
      t: "Image",
      c: [
        node.attr ?? emptyAttr(),
        (node.data?.alt as OutDesignNode[]).map(outDesignInlineToPandoc) ?? [],
        [node.data?.src as string ?? "", node.data?.title as string ?? ""]
      ]
    }
    
    case "Span":
    return {
      t: "Span",
      c: [
        node.attr ?? emptyAttr(),
        (node.children as OutDesignNode[]).map(outDesignInlineToPandoc) ?? []
      ]
    }

    case "Note": 
    return {
      t: "Note",
      c: (node.children as OutDesignNode[]).map(outDesignBlockToPandoc) ?? []
    }
    
    default:
    return {
      t: "Str",
      c: "[NOT SUPPORTED]"
    }
  }
}